import React from 'react';
import _ from 'lodash';
import { IInvoiceService } from '../../services/IInvoiceService';
import HeaderInvoiceList from '../components/HeaderInvoiceList';
import FooterInvoice from '../components/FooterInvoice';
import type { Location } from '@remix-run/router';
import queryString from 'query-string';
import { invoiceOverdue, invoicePaid } from '../../utils/invoice';
import { renderInvoiceExceededMaxPaymentLimit, renderInvoiceListBodyHeader } from './ViewInvoiceListContentHeader';
import { centNumber, dollarNumber } from '../../utils/Currency';
import { format } from 'date-fns';
import './ViewInvoiceList.css';
import { IAnalyticsService } from '../../services/IAnalyticsService';
import { EventName, InvoiceListErrorType } from '../../enum/AnalyticsEnum';

const MAX_TIME_IN_MILLISECONDS = 3600000;
const offlineInvoicePopoverText = `This invoice can't be viewed online. Contact`;
const MAX_PAYABLE = 99999;

export type ViewInvoiceListProps = {
    readonly getInvoiceList: IInvoiceService['getInvoiceList']
    readonly postInvoiceList: IInvoiceService['postInvoiceList']
    readonly notifyEvent: IAnalyticsService['notifyEvent']
    readonly params?: any
    readonly useLocation?: Location
    readonly navigateToPage?: (link: string) => void
}

const SelectedInvoice: string[] = [];

enum SortStatus {
    AscendDueDate,
    ReverseDueDate,
    AscendStatus,
    ReverseStatus,
    None
}

export type ViewInvoiceListState = {
    readonly invoiceListData: any
    readonly loading: boolean
    readonly error: boolean
    readonly recording: boolean
    readonly isPaymentPage: boolean
    readonly isTimeout: boolean
    readonly bulkPaymentError: boolean
    readonly invoicesToPay: number
    readonly tooMuchSelected: boolean
    readonly payDisabled: boolean
    readonly allSelected: boolean
    readonly paymentInitializing: boolean
    readonly orderBy: SortStatus
}

export class ViewInvoiceList extends React.Component<ViewInvoiceListProps, ViewInvoiceListState> {
    constructor(props) {
        super(props);
        this.state = {
            invoiceListData: null,
            loading: true,
            error: false,
            recording: false,
            isPaymentPage: false,
            isTimeout: false,
            bulkPaymentError: false,
            invoicesToPay: 0,
            tooMuchSelected: false,
            payDisabled: true,
            allSelected: false,
            paymentInitializing: false,
            orderBy: SortStatus.None
        };
    }

    userActionHistory: string[] = [];

    componentDidUpdate(prevProps) {
        if (prevProps.params != this.props.params) {
            this.setState({
                ...this.state,
                loading: true
            });
            const { invoiceListId } = this.props.params;
            const source = _.get(this.props, 'useLocation.search', '');
            this.onGetInvoiceList(invoiceListId, source);
        }
    }

    componentDidMount() {
        const { invoiceListId } = this.props.params;
        const source = _.get(this.props, 'useLocation.search', '');
        this.onGetInvoiceList(invoiceListId, source);
        setTimeout(() => {
            this._errorTracking(InvoiceListErrorType.TIMEOUT, undefined);
            this.setState({
                ...this.state,
                isTimeout: true
            });
        }, this.getMaxTimeout());
    }

    getMaxTimeout = () => {
        return MAX_TIME_IN_MILLISECONDS;
    };

    render() {
        const {
            invoiceListData,
            loading,
            error,
            recording,
            isPaymentPage,
            invoicesToPay,
            bulkPaymentError,
            isTimeout
        } = this.state;
        let totalDue = 0;
        let isStatement = false;
        let isShowListPage = false;
        if (!_.isNull(invoiceListData)) {
            totalDue = invoiceListData.invoiceListTotalAmount;
            isStatement = invoiceListData.isStatement;
            isShowListPage = !loading && !error && !bulkPaymentError && !invoiceListData.isExpired && !_.isEmpty(invoiceListData.data) && !invoiceListData.invoiceListError && !isTimeout;
        }

        return (
            <div>
                <HeaderInvoiceList error={error}
                                   loading={loading}
                                   showListPage={isShowListPage}
                                   isStatement={isStatement}
                                   isPaymentPage={isPaymentPage}
                                   recording={recording}
                                   invoicesToPay={invoicesToPay}
                                   invoiceList={invoiceListData}
                                   totalDue={totalDue}
                                   isTimedOut={isTimeout}
                                   bulkPaymentError={bulkPaymentError}
                                   fsPlatformError={false}
                />
                {isShowListPage && this.renderInvoiceListTable()}

                <FooterInvoice country={_.get(this.state, 'invoiceListData.country', 'AU')} />
            </div>
        );
    }

    renderInvoiceListTable = () => {
        const onlineList = this.state.invoiceListData.data.filter(invoice => invoice.allowOnlinePayment !== false); // todo: mock
        const hasOnlinePayments = onlineList.length != 0;
        const { invoiceListData, payDisabled, paymentInitializing } = this.state;
        return (
            <div className="container">
                <div className="invoice-list-table">
                    {hasOnlinePayments && (
                        <div style={{ overflow: 'hidden' }}>
                            {renderInvoiceExceededMaxPaymentLimit(this.state.tooMuchSelected)}
                            {renderInvoiceListBodyHeader(invoiceListData, paymentInitializing, payDisabled, this.paySelectedInvoices)}
                        </div>
                    )}
                    {this.renderInvoiceTable(hasOnlinePayments)}
                </div>
            </div>
        );
    };

    _errorTracking = (errorType, invoiceList) => {
        const { notifyEvent, useLocation } = this.props;
        if (invoiceList == undefined) {
            invoiceList = this.state.invoiceListData;
        }
        const search = useLocation && useLocation?.search ? useLocation.search : '';
        const query = queryString.parse(search);
        const source = _.get(query, 'source', '');

        const userId = invoiceList.consumer + '/' + invoiceList.companyId;
        const activityData = {
            userId,
            companyId: invoiceList.companyId,
            consumer: invoiceList.consumer,
            source,
            history: this.userActionHistory,
            invoiceMeta: invoiceList.invoiceMeta,
            customerId: invoiceList.customerId,
            errorType,
            invoiceListCount: invoiceList.invoiceListCount || 0,
            isStatement: invoiceList?.isStatement,
            offlineInvoiceListCount: invoiceList.offlineInvoiceListCount || 0
        };
        notifyEvent(EventName.INVOICE_LIST_ERROR, activityData);
    };

    renderInvoiceTable = (hasOnlinePayments) => {
        const { invoiceListData } = this.state;
        const total = parseFloat(invoiceListData.invoiceListTotalAmount.toFixed(2));
        let orderInvoiceList = invoiceListData.data;
        if (this.state.orderBy === SortStatus.AscendStatus || this.state.orderBy === SortStatus.ReverseStatus) {
            const order = this.state.orderBy === SortStatus.ReverseStatus ? 'desc' : 'asc';
            orderInvoiceList = _.orderBy(invoiceListData.data, ['daysDue', 'invoiceNumber'], [order, order]);
        }
        if (this.state.orderBy === SortStatus.AscendDueDate || this.state.orderBy === SortStatus.ReverseDueDate) {
            const order = this.state.orderBy === SortStatus.ReverseDueDate ? 'asc' : 'desc';
            orderInvoiceList = _.orderBy(invoiceListData.data, ['dueDate', 'invoiceNumber'], [order, order]);
        }

        return (
            <div className="feelix-card" style={{ marginBottom: 0 }}>
                <table className="table-alt">
                    {this.renderInvoiceTitle(hasOnlinePayments)}
                    {this.renderInvoiceMobileBody(hasOnlinePayments, orderInvoiceList)}
                    {this.renderInvoiceMobileFoot(total)}
                    {this.renderInvoiceBody(hasOnlinePayments, orderInvoiceList)}
                    {this.renderInvoiceFoot(hasOnlinePayments, total)}
                </table>
            </div>
        );
    };

    renderInvoiceTitle = (hasOnlinePayments) => {
        return (
            <thead>
            <tr>
                {hasOnlinePayments && <th className="tickbox-column">
                    <input id="someId" type="checkbox" checked={this.state.allSelected} className="checkbox-inline"
                           onChange={(status) => this.selectAllInvoices(status)} />
                </th>}
                <th data-field="invoiceNumber" colSpan={2} style={{ width: '30%' }}>Number</th>
                <th data-field="dueDate" style={{ width: '25%' }}>Due date<i className="icon-caret"
                                                                             onClick={this.orderByBueDate}>
                </i></th>
                <th data-field="status">Status <i className="icon-caret" onClick={this.orderByStatus}></i>
                </th>
                <th data-field="amountDue" className="text-right">Due amount ($)</th>
            </tr>
            </thead>
        );
    };

    renderInvoiceMobileBody = (hasOnlinePayments, orderInvoiceList) => {
        const onlinePaymentStr = 'Online Payments aren\'t available for this invoice.';
        return (
            <tbody className="tbody-mobile">
            {
                orderInvoiceList.map(invoice => {
                    return (
                        <tr key={invoice.invoiceId}>
                            <td style={{ verticalAlign: 'middle' }} className="tickbox-column">
                                <input
                                    className={invoice.allowOnlinePayment ? 'checkbox-inline' : 'checkbox-inline-disabled'}
                                    checked={invoice.selected}
                                    type="checkbox" onChange={(status) => this.selectInvoice(invoice, status)}
                                    disabled={!invoice.allowOnlinePayment} />
                                {!invoice.allowOnlinePayment &&
                                    <div style={{ position: 'relative' }}>
                                        <span className="invoiceTooltip">{onlinePaymentStr}<br /> Please view the invoice for payment details.</span>
                                    </div>
                                }
                            </td>
                            <td style={{ paddingLeft: '0px' }}
                                onClick={() => this.viewInvoice(invoice)}>
                                <p>{invoice.invoiceNumber}</p>
                                {invoice.dueAmount === 0 && <p>
                                    <span className="due-amount">$0.00</span>
                                </p>}
                                {invoice.dueAmount != 0 && <p>
                                    <span
                                        className="due-amount">${dollarNumber(invoice.dueAmount)}{centNumber(invoice.dueAmount)}</span>
                                </p>}
                                <div className="status-desc">
                                    <p className={this.statusClass(invoice)}>{this.computeDueDateDescription(invoice)?.prefix}
                                        <span
                                            style={{ fontWeight: 'bold' }}>{this.computeDueDateDescription(invoice)?.description}</span>
                                    </p>
                                </div>
                            </td>
                            {invoice.isOffline &&
                                <td className="tag-column" style={{ verticalAlign: 'middle' }}>
                                    <span className="feelix-label"
                                          style={{ float: 'right' }}>No details available</span>
                                    <span className="invoiceTagTooltip">{offlineInvoicePopoverText}</span>
                                </td>}
                            {!invoice.isOffline &&
                                <td style={{ verticalAlign: 'middle' }}
                                    onClick={() => this.viewInvoice(invoice)}>
                                    <div className="open-icon viewInvoiceLink"></div>
                                </td>
                            }

                        </tr>
                    );
                })
            }
            </tbody>
        );
    };

    renderInvoiceMobileFoot = (total) => {
        return (
            <tfoot className="tfoot-mobile">
            <tr className="totalLine">
                <td colSpan={2}>
                    <p>Total due</p>
                    {total === 0 && <span className="totalDue">$0.00</span>}
                    {total != 0 && <span className="totalDue">{dollarNumber(total)}{centNumber(total)}</span>}
                </td>
            </tr>
            </tfoot>
        );
    };

    renderInvoiceFoot = (hasOnlinePayments, total) => {
        return (
            <tfoot className="tfoot-desktop">
            <tr className="totalLine">
                {hasOnlinePayments && <td colSpan={5}></td>}
                {!hasOnlinePayments && <td colSpan={4}></td>}
                <td>
                    {total === 0 && <span className="totalDue">0.00</span>}
                    {total != 0 && <span className="totalDue">{dollarNumber(total)}{centNumber(total)}</span>}
                </td>
            </tr>
            </tfoot>
        );
    };


    renderInvoiceBody = (hasOnlinePayments, orderInvoiceList) => {
        const onlinePaymentStr = 'Online Payments aren\'t available for this invoice.';

        return (
            <tbody className="tbody-desktop">
            {
                orderInvoiceList.map(invoice => {
                    return (
                        <tr key={invoice.invoiceId}>
                            {hasOnlinePayments && (
                                <td className="tickbox-column">
                                    <input
                                        className={invoice.allowOnlinePayment ? 'checkbox-inline' : 'checkbox-inline-disabled'}
                                        checked={invoice.selected}
                                        type="checkbox" onChange={(status) => this.selectInvoice(invoice, status)}
                                        disabled={!invoice.allowOnlinePayment} />
                                    <div style={{ position: 'relative' }}>
                                        {!invoice.allowOnlinePayment &&
                                            <span className="invoiceTooltip">{onlinePaymentStr} <br /> Please view the invoice for payment details.</span>}
                                    </div>
                                </td>)}
                            {invoice.isOffline && <td>{invoice.invoiceNumber}</td>}
                            {!invoice.isOffline && <td><a onClick={() => this.viewInvoice(invoice)}>
                                {invoice.invoiceNumber}</a></td>}
                            {invoice.isOffline && <td className="tag-column">
                                <span className="feelix-label">No details available</span>
                                <span className="invoiceTagTooltip">{offlineInvoicePopoverText}</span>
                            </td>}
                            {!invoice.isOffline && <td></td>}
                            <td>{format(new Date(invoice.dueDate), 'dd/MM/y')}</td>
                            {invoice.dueAmount === 0 && <td className="text-right">0.00</td>}
                            <td className="status-desc">
                                <div
                                    className={this.statusClass(invoice)}>{this.computeDueDateDescription(invoice)?.prefix}
                                    <span
                                        style={{ fontWeight: 'bold' }}>{this.computeDueDateDescription(invoice)?.description}</span>
                                </div>
                            </td>
                            {invoice.dueAmount != 0 &&
                                <td
                                    className="text-right">{dollarNumber(invoice.dueAmount)}{centNumber(invoice.dueAmount)}</td>}
                        </tr>
                    );
                })
            }
            </tbody>
        );
    };

    computeDueDateDescription = invoice => {
        if (invoicePaid(invoice)) {
            return {
                prefix: '',
                description: 'Paid'
            };
        }
        // We use Math.sign because it handles string as well as integers
        const daysUntilDueSign = Math.sign(invoice.daysDue);
        const daysUntilDueAbsolute = Math.abs(invoice.daysDue);
        const dayOrDays = daysUntilDueAbsolute === 1 ? 'day' : 'days';
        if (daysUntilDueSign === 0) {
            return {
                prefix: 'Due ',
                description: 'today'
            };
        }
        if (daysUntilDueSign > 0) {
            return {
                prefix: 'Due in ',
                description: `${daysUntilDueAbsolute} ${dayOrDays}`
            };
        }
        if (daysUntilDueSign < 0) {
            return {
                prefix: 'Overdue by ',
                description: `${daysUntilDueAbsolute} ${dayOrDays}`
            };
        }

    };

    statusClass = invoice => {
        if (invoicePaid(invoice)) {
            invoice.statusNumber = 2;
            return 'paid';
        }
        if (invoiceOverdue(invoice)) {
            invoice.statusNumber = 0;
            return 'overdue';
        }
        invoice.statusNumber = 1;
        return 'due';
    };


    orderByBueDate = () => {
        if (this.state.orderBy != SortStatus.ReverseDueDate) {
            this.setState({
                ...this.state,
                orderBy: SortStatus.ReverseDueDate
            });
            return;
        }
        this.setState({
            ...this.state,
            orderBy: SortStatus.AscendDueDate
        });

    };

    orderByStatus = () => {
        if (this.state.orderBy != SortStatus.ReverseStatus) {
            this.setState({
                ...this.state,
                orderBy: SortStatus.ReverseStatus
            });
            return;
        }
        this.setState({
            ...this.state,
            orderBy: SortStatus.AscendStatus
        });
    };

    viewInvoice = (invoice) => {
        const invoiceUrl = `/pay/#/${invoice.public_url}${this.props.useLocation?.search}`;
        window.open(invoiceUrl, '_blank');
        this.userActionHistory.push(EventName.VIEW_INVOICE);
    };

    paySelectedInvoices = () => {
        const invoiceList = this.state.invoiceListData;
        this.setState({
            ...this.state,
            paymentInitializing: true
        });
        const filteredInvoices = invoiceList.data.filter(invoice => invoice.selected === true);
        const invoicesTotal = _.reduce(filteredInvoices, function(sum, invoice) {
            return sum + invoice.dueAmount;
        }, 0);
        const selectedInvoiceIdList = filteredInvoices.map(invoice => invoice.invoiceId);
        SelectedInvoice.push(...selectedInvoiceIdList);
        this.props.postInvoiceList({
            consumer: invoiceList.consumer,
            invoices: filteredInvoices,
            country: invoiceList.country,
            companyId: invoiceList.companyId,
            companyName: invoiceList.companyName,
            customerId: invoiceList.customerId,
            customerName: invoiceList.customerName,
            total: parseFloat(invoicesTotal.toFixed(2)),
            isStatement: invoiceList.isStatement,
            currency: invoiceList.invoiceListCurrency
        }).then(bulkPaymentDetails => {
            const { useLocation, notifyEvent, params, navigateToPage } = this.props;
            const search = useLocation && useLocation?.search ? useLocation.search : '';
            const query = queryString.parse(search);
            const source = _.get(query, 'source', '');
            this.userActionHistory.push(EventName.PAY_NOW_BUTTON);

            const activityData = {
                bulkPaymentRecord: bulkPaymentDetails,
                source,
                history: this.userActionHistory,
                invoiceMeta: invoiceList.invoiceMeta
            };

            notifyEvent(EventName.PAY_NOW_BUTTON, activityData, true);

            navigateToPage && navigateToPage(`/invoicelist/${params.invoiceListId}/${bulkPaymentDetails.id}/pay${useLocation?.search}`);
        });
    };

    selectAllInvoices = (status) => {
        const invoiceList = this.state.invoiceListData;
        invoiceList.data.filter(invoice => invoice.allowOnlinePayment === true).forEach(invoice => {
            invoice.selected = status.target.checked;
        });
        this.selectPayable(invoiceList);
    };

    selectInvoice = (invoice, status) => {
        const invoiceListData = this.state.invoiceListData;
        invoiceListData.data.filter(invoiceData => invoiceData.public_url === invoice.public_url).forEach(invoice => {
            invoice.selected = status.target.checked;
        });
        this.selectPayable(invoiceListData);
    };

    selectPayable = (invoiceListData) => {
        const selectInvoiceData = invoiceListData.data.filter(invoice => invoice.selected === true);
        const invoicesTotal = _.reduce(selectInvoiceData, function(sum, invoice) {
            return sum + invoice.dueAmount;
        }, 0);
        let tooMuchSelected = false;
        let payDisabled = true;
        if (selectInvoiceData.length > 0) {
            tooMuchSelected = invoicesTotal > MAX_PAYABLE;
            payDisabled = invoicesTotal > MAX_PAYABLE;
        }
        if (tooMuchSelected) {
            this.warningTracking(invoiceListData, 'exceededMaxPaymentLimit');
        }
        const allSelected = selectInvoiceData.length === invoiceListData.data.filter(invoice =>
            invoice.allowOnlinePayment === true).length;

        this.setState({
                ...this.state,
                loading: false,
                error: false,
                invoiceListData: invoiceListData,
                tooMuchSelected,
                payDisabled: payDisabled,
                allSelected
            }
        );
    };

    warningTracking = (invoiceList, warningType) => {
        const userId = invoiceList.consumer + '/' + invoiceList.companyId;
        const campaign = {
            utm_source: 'invoice-list',
            utm_medium: 'presentment',
            utm_campaign_name: 'view-invoice',
            utm_term: 'open-invoices',
            utm_content: 'invoice-list'
        };
        const { useLocation, notifyEvent } = this.props;
        const search = useLocation && useLocation?.search ? useLocation.search : '';
        const query = queryString.parse(search);
        const source = _.get(query, 'source', '');
        const isStatement = invoiceList && invoiceList.isStatement;
        const activityData = {
            userId,
            companyId: invoiceList.companyId,
            consumer: invoiceList.consumer,
            source,
            campaign,
            history: this.userActionHistory,
            invoiceMeta: invoiceList.invoiceMeta,
            customerId: invoiceList.customerId,
            warningType,
            invoicesCount: invoiceList.invoiceListCount || 0,
            isStatement: isStatement,
            offlineInvoicesCount: invoiceList.offlineInvoiceListCount || 0
        };
        notifyEvent(EventName.INVOICE_LIST_WARNING, activityData);
    };

    onGetInvoiceList = (hash: string, source: string) => {
        this.props.getInvoiceList(hash, source).then(invoiceResponse => {
            const invoiceList = invoiceResponse.data;
            this.invoiceListTracking(invoiceList);
            if (!_.isEmpty(SelectedInvoice)) {
                invoiceList.data.forEach(invoice => {
                    if (invoice && SelectedInvoice.includes(invoice.invoiceId)) {
                        invoice.selected = true;
                    }
                });
                SelectedInvoice.length = 0;
            }
            this.selectPayable(invoiceList);
        }).catch(error => {
            this.setState((state) => ({
                ...state,
                loading: false,
                error: true
            }));
            console.error('get invoice error' + error.message, error.stack);
        });
    };

    invoiceListTracking = (invoiceList: any) => {
        if (_.isEmpty(invoiceList.data) && !invoiceList.invoiceListError && !invoiceList.isExpired) {
            this._errorTracking(InvoiceListErrorType.EMPTY_INVOICE_LIST, invoiceList);
        } else if (invoiceList.isExpired) {
            this._errorTracking(InvoiceListErrorType.EXPIRED_LINK, invoiceList);
        } else if (invoiceList.invoiceListError) {
            this._errorTracking(InvoiceListErrorType.ERROR_GETTING_INVOICE, invoiceList);
        } else if ((invoiceList.data && invoiceList.data.length > 0) && !invoiceList.isExpired && invoiceList.isPrivate === 'false') {
            this._trackViewInvoiceList(invoiceList);
        }
    };

    _trackViewInvoiceList = (invoiceList: any) => {
        const { notifyEvent } = this.props;
        this.userActionHistory.push(EventName.VIEW_INVOICE_LIST);
        const userId = invoiceList.consumer + '/' + invoiceList.companyId;
        const activityData = {
            userId: userId,
            company: {
                id: invoiceList.companyId
            },
            customer: {
                id: invoiceList.customerId
            },
            consumer: invoiceList.consumer,
            invoice: {},
            source: 'email',
            history: this.userActionHistory,
            invoiceListId: invoiceList.invoiceMeta,
            invoiceListCount: invoiceList.invoiceListCount,
            invoiceListTotalAmount: parseFloat(invoiceList.invoiceListTotalAmount.toFixed(2)),
            isStatement: invoiceList.isStatement,
            offlineInvoiceListCount: invoiceList.offlineInvoiceListCount,
            offlineInvoiceListTotalAmount: parseFloat(invoiceList.offlineInvoiceListTotalAmount.toFixed(2))
        };
        notifyEvent(EventName.VIEW_INVOICE_LIST, activityData);
    };
}

export default ViewInvoiceList;

